import { useEffect, useRef, useState } from 'react';

import type { CategoryPredictionSource } from '@splitfiesta/shared';

/**
 * Minimum shape the scheduler needs from a prediction. Apps may carry
 * more fields; the scheduler only reads `category`, `confidence` and
 * `source` (for the trust gate) and passes the object through.
 */
export interface ScheduledPrediction {
  category: string;
  confidence: number;
  subcategory?: { type: string; confidence: number };
  source: CategoryPredictionSource;
  /** Categories the free tier scored, most likely first; feeds the form's category row. */
  ranked?: readonly string[];
}

/**
 * Result of one paid-tier attempt.
 *  - `answered`: a confident, in-taxonomy answer. Applied if the title
 *    is still current, and remembered so a return to the title restores
 *    it without paying again.
 *  - `no-answer`: nothing to apply and nothing to retry — a gate was
 *    closed (anonymous, disabled, opted out) or the model wasn't
 *    confident enough. Remembered too: below-floor answers are not
 *    cached server-side, so a re-fetch would be a second real payment.
 *  - `failed`: the request didn't complete (network, 5xx, or the API's
 *    409 for a superseded request). The next flush, or the next visit
 *    to this title, retries — up to `maxCloudAttempts` per title.
 */
export type CloudTierOutcome<
  P extends ScheduledPrediction = ScheduledPrediction,
> =
  | { status: 'answered'; prediction: P }
  | { status: 'no-answer' }
  | { status: 'failed' };

/**
 * Keyword matches at or above this confidence are trusted and the paid
 * tier is skipped. The keyword detector caps at 0.9 (0.8 base + 0.1
 * length bonus), so ≥ 0.8 means a clear-winner match; it returns 0.65
 * on close ties, which we still send to the cloud to disambiguate.
 */
export const KEYWORD_TRUST_THRESHOLD = 0.8;

/**
 * On-device LLM answers self-report confidence and can be miscalibrated
 * (Foundation Models scoring "Hofer" as accommodation at 0.80 because it
 * lacks the world knowledge to recognise the Austrian grocery chain).
 * Genuine strong matches typically score 0.85+.
 */
export const NATIVE_TRUST_THRESHOLD = 0.85;

/**
 * Titles shorter than this are not classified by any tier, on any
 * platform. Two, not three: the app's languages have real two-letter
 * expense words ("té", "Öl"), the cloud classifies them fine, and a
 * two-character junk title costs at most one cheap request.
 */
export const MIN_PREDICTION_TITLE_CHARS = 2;

/** The free tier's answer is good enough to skip paying for the cloud. */
export function isTrustedLivePrediction(p: ScheduledPrediction): boolean {
  if (p.category === 'other') return false;
  if (p.source === 'keyword') return p.confidence >= KEYWORD_TRUST_THRESHOLD;
  if (p.source === 'native') return p.confidence >= NATIVE_TRUST_THRESHOLD;
  return false;
}

export interface UseCategoryPredictionSchedulerOptions<
  P extends ScheduledPrediction,
> {
  title: string;
  /** false = nothing runs (manual chip pick; Edit while the title equals the loaded one). */
  enabled: boolean;
  /** Bump to run both tiers now (title blur on web, keyboard dismiss on mobile). */
  flushNonce: number;
  /** Free tier: keyword (+ on-device LLM on mobile). `null` = nothing for this title. A throw counts as `null`. */
  runLive: (title: string) => Promise<P | null>;
  /** Paid tier. Never emits; the scheduler applies `answered` if the title is still current. A throw counts as `failed`. */
  runCloud: (title: string) => Promise<CloudTierOutcome<P>>;
  /**
   * Single sink for both tiers. `null` means "no prediction for the
   * current title" (too short, or the free tier had nothing) — clear the
   * chip. Which tier answered is visible on `prediction.source`.
   */
  onPrediction: (prediction: P | null) => void;
  /** Titles shorter than this are not classified. Default `MIN_PREDICTION_TITLE_CHARS`. */
  minTitleLength?: number;
  /** Free tier debounce after the last keystroke. Default 300. */
  liveDebounceMs?: number;
  /** Paid tier idle delay after the last keystroke. Default 1500. */
  cloudIdleMs?: number;
  /**
   * Upper bound on one free-tier run. Mobile's on-device model has no
   * timeout of its own; past this the run counts as `null` for the title
   * (the paid tier proceeds) and the model is not asked again until the
   * title changes. Default 5000.
   */
  liveTimeoutMs?: number;
  /**
   * How many `failed` paid-tier attempts a title gets before it is
   * treated as declined until the title changes (or the field is
   * cleared, which forgets every title's memory). Bounds the doomed
   * requests an offline user would otherwise fire on every keyboard
   * dismiss. Default 2.
   */
  maxCloudAttempts?: number;
  /**
   * Upper bound on one paid-tier request; past it the attempt counts as
   * `failed`. Bounds how long a caller that holds back the placeholder
   * waits on a slow endpoint. Default 8000.
   */
  cloudTimeoutMs?: number;
}

export interface CategoryPredictionSchedulerState {
  /** Free tier in flight for the current title. Callers OR it with `isCloudPending` for the "Categorizing…" indicator. */
  isLivePending: boolean;
  /**
   * The paid tier is expected to answer for the current title and has
   * not concluded yet: on from the moment the free tier settles with an
   * untrusted answer (including during the idle wait), off once the
   * paid tier answered, declined, exhausted its attempts, or was
   * skipped. Lets a caller show "Categorizing…" instead of a
   * placeholder chip while a better answer is on its way.
   */
  isCloudPending: boolean;
  /**
   * A prediction (live or cloud) has settled for the current trimmed
   * title. Resets on every title change; callers that want a sticky
   * "some category is showing" flag (web's Save gate) derive their own.
   */
  isSettled: boolean;
}

/** What the paid tier knows about one title. */
interface CloudMemory<P extends ScheduledPrediction> {
  /** Set while the request is in flight. */
  promise: Promise<CloudTierOutcome<P>> | null;
  /** Set once the request resolved. */
  outcome: CloudTierOutcome<P> | null;
  /** Requests started for this title. */
  attempts: number;
}

/** Recent titles the paid tier remembers; older ones hit the server cache again. */
const MAX_REMEMBERED_TITLES = 16;

/**
 * The two-tier category-prediction state machine shared by web's expense
 * form and both mobile expense screens.
 *
 *  - Free tier `liveDebounceMs` after the last keystroke. Safe to run on
 *    every pause; never the network on web.
 *  - Paid tier `cloudIdleMs` after the last keystroke, or immediately on
 *    flush. Each keystroke resets both timers, so a typing burst pays
 *    once, for the final title. Skipped when the free tier's answer is
 *    trusted (`isTrustedLivePrediction`), when the title already reached
 *    the paid tier, or while disabled. The paid tier always waits for
 *    the free tier's answer first — on mobile that includes the 1–2 s
 *    on-device inference, bounded by `liveTimeoutMs` — so a trusted
 *    on-device answer can suppress the payment; the cost is that a
 *    keyword-miss title's cloud upgrade lands after the inference, not
 *    concurrently with it.
 *  - Results apply only while their title is the current, enabled title
 *    of a mounted host. Staleness is keyed on the title, not on the
 *    effect run: a flush or an enable/disable toggle re-runs the effect
 *    without changing the title, and an answer that lands afterwards
 *    must still apply (the keyboard is often dismissed while the
 *    on-device tier runs).
 *  - Each tier runs at most once per title while its run is in flight,
 *    even across a keystroke-and-backspace detour. The paid tier also
 *    remembers recent titles' outcomes (the last `MAX_REMEMBERED_TITLES`
 *    used): an answer is restored on return with no `other` flicker and
 *    no second payment, a declined title is not re-fetched, a failed one
 *    (including a request past `cloudTimeoutMs`) is retried up to
 *    `maxCloudAttempts`.
 *  - On a flush before the free tier settled, the free tier runs inline
 *    first so a keyword-answerable title never pays.
 *  - Clearing the title forgets both tiers' memory, so a retype is
 *    classified afresh (with a fresh paid-tier budget) and the Save gate
 *    stays closed until it is.
 *
 * The hook owns no chip state: everything reaches the caller through
 * `onPrediction`, always from a timer or a resolved promise, never from
 * the effect body. The returned flags are derived from "which title is
 * pending / settled", so no state is set synchronously inside effects.
 */
export function useCategoryPredictionScheduler<P extends ScheduledPrediction>(
  options: UseCategoryPredictionSchedulerOptions<P>
): CategoryPredictionSchedulerState {
  const {
    title,
    enabled,
    flushNonce,
    minTitleLength = MIN_PREDICTION_TITLE_CHARS,
    liveDebounceMs = 300,
    cloudIdleMs = 1500,
    liveTimeoutMs = 5000,
    maxCloudAttempts = 2,
    cloudTimeoutMs = 8000,
  } = options;
  const trimmed = title.trim();
  const longEnough = trimmed.length >= minTitleLength;

  // Latest callbacks, read at fire time so the effect's identity depends
  // only on the inputs that should reschedule it.
  const runLiveRef = useRef(options.runLive);
  const runCloudRef = useRef(options.runCloud);
  const onPredictionRef = useRef(options.onPrediction);
  useEffect(() => {
    runLiveRef.current = options.runLive;
    runCloudRef.current = options.runCloud;
    onPredictionRef.current = options.onPrediction;
  });

  // Derived flags: which trimmed title the free tier is pending / has
  // settled for. Comparing against the current title makes a title
  // change reset both without any effect-time setState.
  const [pendingTitle, setPendingTitle] = useState<string | null>(null);
  const [settledTitle, setSettledTitle] = useState<string | null>(null);
  // The title the paid tier still owes an outcome for.
  const [cloudPendingTitle, setCloudPendingTitle] = useState<string | null>(
    null
  );

  // The title results may currently apply to; `null` while disabled,
  // too short, or unmounted. Every await re-checks it.
  const activeTitleRef = useRef<string | null>(null);
  useEffect(
    () => () => {
      activeTitleRef.current = null;
    },
    []
  );

  // Free tier: the settled answer for the active title, and every run
  // still in flight (by title) so a run is never started twice.
  const liveRef = useRef<{ title: string; prediction: P | null } | null>(null);
  const liveInFlightRef = useRef(new Map<string, Promise<P | null>>());
  // Paid tier: recent titles with their in-flight request or outcome.
  // Survives title changes so a return to a title reuses it.
  const cloudMemoryRef = useRef(new Map<string, CloudMemory<P>>());
  // What the sink last received (a non-null prediction), so a restore
  // knows whether it is needed and a live result never overwrites a
  // cloud answer for the same title.
  const chipRef = useRef<{ title: string; fromCloud: boolean } | null>(null);
  // Short-title `null` is emitted once per title.
  const nullEmittedForRef = useRef<string | null>(null);
  const flushSeenRef = useRef(flushNonce);

  useEffect(() => {
    // Record the flush before any early return so a flush while the
    // title is short doesn't fire the paid tier instantly on the next
    // keystroke.
    const isFlush = flushNonce !== flushSeenRef.current;
    flushSeenRef.current = flushNonce;

    if (!enabled) {
      activeTitleRef.current = null;
      return;
    }

    if (!longEnough) {
      activeTitleRef.current = null;
      if (nullEmittedForRef.current === trimmed) return;
      const timer = setTimeout(() => {
        nullEmittedForRef.current = trimmed;
        // The chip is cleared, and everything both tiers knew is
        // forgotten with it: a retype is classified afresh — including a
        // fresh paid-tier budget, so a title that exhausted its retries
        // while offline is reachable again — and stays unsettled until
        // it is.
        liveRef.current = null;
        chipRef.current = null;
        cloudMemoryRef.current.clear();
        setSettledTitle(null);
        onPredictionRef.current(null);
      }, 0);
      return () => clearTimeout(timer);
    }
    nullEmittedForRef.current = null;
    activeTitleRef.current = trimmed;
    const isCurrent = () => activeTitleRef.current === trimmed;

    if (liveRef.current && liveRef.current.title !== trimmed) {
      liveRef.current = null;
    }

    const cloudAnswerOnChip = () =>
      chipRef.current?.title === trimmed && chipRef.current.fromCloud;

    const cloudMemory = () => cloudMemoryRef.current.get(trimmed);

    /** Insert or refresh this title's memory, evicting the oldest past the cap. */
    const remember = (memory: CloudMemory<P>) => {
      const memories = cloudMemoryRef.current;
      memories.delete(trimmed);
      memories.set(trimmed, memory);
      if (memories.size > MAX_REMEMBERED_TITLES) {
        // Never evict a title whose request is still running: its
        // resolver would write the outcome to a detached object and the
        // answer would be lost.
        for (const [key, entry] of memories) {
          if (entry.promise === null) {
            memories.delete(key);
            break;
          }
        }
      }
    };

    /** The remembered paid answer for this title, if any. */
    const rememberedAnswer = (): P | null => {
      const outcome = cloudMemory()?.outcome;
      return outcome?.status === 'answered' ? outcome.prediction : null;
    };

    const cloudConcluded = () =>
      setCloudPendingTitle(current => (current === trimmed ? null : current));

    /** Will the paid tier be consulted for this live answer, and has it not concluded yet? */
    const paidTierPendingFor = (live: P | null): boolean => {
      if (live && isTrustedLivePrediction(live)) return false;
      const memory = cloudMemory();
      if (!memory) return true;
      if (memory.promise) return true;
      return (
        memory.outcome?.status === 'failed' &&
        memory.attempts < maxCloudAttempts
      );
    };

    const emit = (answer: P | null, fromCloud: boolean) => {
      // The paid tier names one category; the row after it keeps the
      // free tier's ranking for this title.
      const liveRanked =
        liveRef.current?.title === trimmed
          ? liveRef.current.prediction?.ranked
          : undefined;
      const prediction =
        fromCloud && answer && !answer.ranked && liveRanked
          ? { ...answer, ranked: liveRanked }
          : answer;
      chipRef.current = prediction ? { title: trimmed, fromCloud } : null;
      onPredictionRef.current(prediction);
      setSettledTitle(trimmed);
    };

    /** Run the free tier for this title (once), apply, settle. */
    const settleLive = (): Promise<P | null> => {
      setPendingTitle(trimmed);
      const inFlight = liveInFlightRef.current.get(trimmed);
      if (inFlight) return inFlight;
      const promise = (async () => {
        let prediction: P | null;
        let timeout: ReturnType<typeof setTimeout> | undefined;
        try {
          prediction = await Promise.race([
            runLiveRef.current(trimmed),
            new Promise<null>(resolve => {
              timeout = setTimeout(() => resolve(null), liveTimeoutMs);
            }),
          ]);
        } catch {
          prediction = null;
        } finally {
          clearTimeout(timeout);
        }
        liveInFlightRef.current.delete(trimmed);
        setPendingTitle(current => (current === trimmed ? null : current));
        if (!isCurrent()) return prediction;
        liveRef.current = { title: trimmed, prediction };
        if (cloudAnswerOnChip()) {
          setSettledTitle(trimmed);
        } else {
          emit(prediction, false);
        }
        if (paidTierPendingFor(prediction)) setCloudPendingTitle(trimmed);
        else cloudConcluded();
        return prediction;
      })();
      liveInFlightRef.current.set(trimmed, promise);
      return promise;
    };

    const liveTimer = setTimeout(
      () => {
        const remembered = rememberedAnswer();
        if (remembered) {
          // Back on a title the cloud already answered: restore it, and
          // don't let the free tier flicker `other` over it. Touch the
          // memory so a title in active use is not the first evicted.
          const memory = cloudMemory();
          if (memory) remember(memory);
          if (!cloudAnswerOnChip()) emit(remembered, true);
          return;
        }
        // Already settled for this title (the paid path ran it inline,
        // or this is a flush after it settled) → nothing to do.
        if (liveRef.current?.title === trimmed) return;
        void settleLive();
      },
      isFlush ? 0 : liveDebounceMs
    );

    const cloudTimer = setTimeout(
      async () => {
        if (rememberedAnswer()) return; // the live timer restores it

        let live: P | null;
        if (liveRef.current?.title === trimmed) {
          live = liveRef.current.prediction;
        } else {
          live = await settleLive();
          if (!isCurrent()) return;
        }
        if (live && isTrustedLivePrediction(live)) {
          cloudConcluded();
          return;
        }

        let memory = cloudMemory();
        const retryable =
          memory?.outcome?.status === 'failed' &&
          memory.attempts < maxCloudAttempts;
        if (!memory || retryable) {
          setCloudPendingTitle(trimmed);
          const promise = (async (): Promise<CloudTierOutcome<P>> => {
            let timeout: ReturnType<typeof setTimeout> | undefined;
            try {
              return await Promise.race([
                runCloudRef.current(trimmed),
                new Promise<CloudTierOutcome<P>>(resolve => {
                  timeout = setTimeout(
                    () => resolve({ status: 'failed' }),
                    cloudTimeoutMs
                  );
                }),
              ]);
            } catch {
              return { status: 'failed' };
            } finally {
              clearTimeout(timeout);
            }
          })();
          memory = {
            promise,
            outcome: null,
            attempts: (memory?.attempts ?? 0) + 1,
          };
          remember(memory);
          const outcome = await promise;
          memory.outcome = outcome;
          memory.promise = null;
        } else if (memory.promise) {
          // Another visit to this title started the request; wait for it.
          await memory.promise;
        }
        cloudConcluded();

        if (!isCurrent()) return;
        const answer = rememberedAnswer();
        if (answer && !cloudAnswerOnChip()) emit(answer, true);
      },
      isFlush ? 0 : cloudIdleMs
    );

    return () => {
      clearTimeout(liveTimer);
      clearTimeout(cloudTimer);
    };
  }, [
    trimmed,
    longEnough,
    enabled,
    flushNonce,
    liveDebounceMs,
    cloudIdleMs,
    liveTimeoutMs,
    maxCloudAttempts,
    cloudTimeoutMs,
  ]);

  return {
    isLivePending: enabled && longEnough && pendingTitle === trimmed,
    isCloudPending: enabled && longEnough && cloudPendingTitle === trimmed,
    isSettled: enabled && longEnough && settledTitle === trimmed,
  };
}
